// The capture of a page with its elements marked, to find them without reading a selector. It shows the
// capture of the device the element was found on, marks the selected element alone with an outline and a
// label (the others of its problem are numbered, their labels never covering each other), centres the view
// on it, and lets the capture be enlarged and moved (buttons, arrow keys on the focused capture, dragging).
// It works inside SiteNorma, with no browser extension, and everything it draws is also said in words in
// the element's own guide and list. The capture fits the space it has (its width follows its container and the
// marks are placed in percentages of it, so they stay on their elements whatever the size); around it there is
// nothing but the device switch and one button to enlarge it: the capture is the content, and its instructions
// are for whoever needs them read aloud.
import { DesktopIcon, MobileIcon, ReloadIcon, ZoomInIcon } from '@radix-ui/react-icons';
import { type CSSProperties, type PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { captureUrl, type Device, type PageIssue, type ScanDetail, type ScannedPage } from '../api';
import { elementsOf, locate, type PageElement, placeLabels } from '../element-logic';
import type { Problem } from '../results-logic';
import { Button } from '../ui';
import { PRIORITY_TONE } from './results-parts';

export const DEVICE_WIDTH: Record<Device, number> = { escritorio: 1366, movil: 390 };
/** The capture fits its space by default; enlarging it shows it at twice that width, to be moved around. */
const ENLARGED = 2;
/** Characters of a label on the capture. The text is cut here, not by the stylesheet: a clipped box would hide
 * words as soon as someone widens the spacing of the text (WCAG 1.4.12); the full name is in the list. */
const LABEL_CHARS = 34;

function labelText(index: number, title: string, isSelected: boolean): string {
  if (!isSelected) return String(index + 1);
  const full = `${index + 1} · ${title}`;
  return full.length > LABEL_CHARS ? `${full.slice(0, LABEL_CHARS - 1).trimEnd()}…` : full;
}

export interface Selection {
  problem: Problem<PageIssue>;
  element: PageElement;
}

export function Inspector({
  scan,
  page,
  device,
  onDevice,
  selected,
  onSelect,
  onHeight,
}: {
  scan: ScanDetail;
  page: ScannedPage;
  device: Device;
  onDevice: (device: Device) => void;
  selected: Selection | null;
  onSelect: (elementId: string) => void;
  /** Tells the page how tall the capture is once it has loaded (positions are described against it). */
  onHeight: (device: Device, height: number) => void;
}) {
  const { t } = useTranslation();
  const [enlarged, setEnlarged] = useState(false);
  const zoom = enlarged ? ENLARGED : 1;
  const [heights, setHeights] = useState<Partial<Record<Device, number>>>({});
  // The device whose image has loaded: while the other one loads, the canvas keeps the height it had, so the
  // page does not get shorter for a moment (and, with it, scroll back up).
  const [loadedDevice, setLoadedDevice] = useState<Device | null>(null);
  // Whether the image of each device is coming, shown or could not be loaded; and how many times it was asked
  // again. Nothing is marked on a capture that is not on screen: a mark over an empty background points nowhere.
  const [loadState, setLoadState] = useState<Partial<Record<Device, 'loaded' | 'error'>>>({});
  const [attempts, setAttempts] = useState<Partial<Record<Device, number>>>({});
  const status = loadState[device] ?? 'loading';
  const canvas = useRef<HTMLDivElement>(null);
  const lastCanvasHeight = useRef(0);
  const [frameWidth, setFrameWidth] = useState(0);
  const frame = useRef<HTMLElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(null);
  const width = DEVICE_WIDTH[device];
  const height = heights[device] ?? null;
  const available = page.captures[device];

  // What the frame measures on screen, to turn the size of a label in pixels into page coordinates. The frame
  // only exists while there is a capture of the device, so it is measured again when that changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `available` and `device` decide whether the frame exists.
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const measure = () => setFrameWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [available, device]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured again when the device or its image changes.
  useEffect(() => {
    const element = canvas.current;
    if (!element || loadedDevice !== device) return;
    const measure = () => {
      if (element.offsetHeight > 0) lastCanvasHeight.current = element.offsetHeight;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [device, loadedDevice, available, zoom]);
  const scale = frameWidth > 0 ? (frameWidth * zoom) / width : 1;

  const selectedBox = selected?.element.byDevice[device]?.box ?? null;
  const outcome = selected ? locate(selected.element.byDevice[device], { available, width, height }) : null;

  // The elements to draw: only those of the problem that is open and that fall on the capture. A box past
  // the screenshot (a carousel slide off to the side, or below the height cap) is not marked: drawing it
  // would enlarge the scrollable area with empty grey.
  const marks = useMemo(() => {
    if (!selected) return [];
    return elementsOf(selected.problem).flatMap((element, index) => {
      const occurrence = element.byDevice[device];
      const found = locate(occurrence, { available, width, height });
      return found.ok ? [{ problem: selected.problem, element, index, box: found.box }] : [];
    });
  }, [selected, device, available, width, height]);
  const drawn = marks;

  // Labels: the selected element's first (the best place), then the others; none covers another.
  const labels = useMemo(() => {
    if (!selected || height === null) return new Map<string, { x: number; y: number } | null>();
    const px = (value: number) => value / scale;
    const requests = [...drawn]
      .sort(
        (a, b) =>
          Number(b.element.id === selected.element.id) - Number(a.element.id === selected.element.id) ||
          a.index - b.index,
      )
      .map((mark) => {
        const isSelected = mark.element.id === selected.element.id;
        const text = labelText(mark.index, mark.element.name.title, isSelected);
        return {
          id: mark.element.id,
          box: mark.box,
          width: px(22 + text.length * 7.5),
          height: px(24),
        };
      });
    return placeLabels(requests, { width, height });
  }, [drawn, selected, height, scale, width]);

  // Centre the view on the selected element when it is on the capture; skip ones outside it so the frame
  // does not scroll into empty grey past the screenshot.
  const scrollBox = outcome?.ok ? outcome.box : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: centred again when the device is switched.
  useEffect(() => {
    const element = frame.current;
    if (!element || !scrollBox || height === null || frameWidth === 0) return;
    const canvasWidth = frameWidth * zoom;
    const canvasHeight = (canvasWidth / width) * height;
    const centreX = ((scrollBox.x + scrollBox.width / 2) / width) * canvasWidth;
    const centreY = ((scrollBox.y + scrollBox.height / 2) / height) * canvasHeight;
    element.scrollTo({
      left: centreX - element.clientWidth / 2,
      top: centreY - element.clientHeight / 2,
    });
  }, [device, zoom, height, frameWidth, width, scrollBox]);

  // Clamp to the capture so a box that starts on it but sticks out (wide carousel item) never paints past
  // 100% and cannot enlarge the scrollable area even if overflow clipping were missing.
  const position = (box: { x: number; y: number; width: number; height: number }): CSSProperties => {
    if (height === null) return { display: 'none' };
    const left = Math.max(0, box.x);
    const top = Math.max(0, box.y);
    const right = Math.min(width, box.x + box.width);
    const bottom = Math.min(height, box.y + box.height);
    return {
      left: `${(left / width) * 100}%`,
      top: `${(top / height) * 100}%`,
      width: `${Math.max((right - left) / width, 0.004) * 100}%`,
      height: `${Math.max((bottom - top) / height, 0.002) * 100}%`,
    };
  };

  /** Ask for the capture again (cache-busted): when it failed to load, or when it looks wrong. */
  function reloadCapture() {
    setLoadState((before) => ({ ...before, [device]: undefined }));
    setLoadedDevice((current) => (current === device ? null : current));
    setAttempts((before) => ({ ...before, [device]: (before[device] ?? 0) + 1 }));
  }

  // Moving the capture by dragging; a click that did not move still selects the element under it.
  const start = (event: PointerEvent<HTMLElement>) => {
    const element = frame.current;
    if (!element || event.button !== 0) return;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: element.scrollLeft,
      top: element.scrollTop,
      moved: false,
    };
  };
  const move = (event: PointerEvent<HTMLElement>) => {
    const state = drag.current;
    const element = frame.current;
    if (!state || !element) return;
    const dx = event.clientX - state.x;
    const dy = event.clientY - state.y;
    if (!state.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
    state.moved = true;
    element.scrollLeft = state.left - dx;
    element.scrollTop = state.top - dy;
  };
  const end = () => {
    window.setTimeout(() => {
      drag.current = null;
    }, 0);
  };

  return (
    <section className="inspector" aria-labelledby="inspector-titulo">
      <div className="inspector-head">
        <h3 id="inspector-titulo" className="sr-only">
          {t('inspector.title')}
        </h3>
        <ToggleButtonGroup
          aria-label={t('inspector.device')}
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[device]}
          onSelectionChange={(keys) => onDevice([...keys][0] === 'movil' ? 'movil' : 'escritorio')}
          className="device-toggle"
        >
          <ToggleButton id="escritorio" className="device-button">
            <DesktopIcon aria-hidden="true" />
            {t('inspector.devices.escritorio')}
          </ToggleButton>
          <ToggleButton id="movil" className="device-button">
            <MobileIcon aria-hidden="true" />
            {t('inspector.devices.movil')}
          </ToggleButton>
        </ToggleButtonGroup>
        {available && (
          <div className="inspector-head-actions">
            <Button variant="secondary" onPress={reloadCapture}>
              <ReloadIcon aria-hidden="true" />
              {t('inspector.reload')}
            </Button>
            <ToggleButton
              isSelected={enlarged}
              onChange={setEnlarged}
              className="device-button enlarge-button"
              aria-label={t('inspector.zoom.label')}
            >
              <ZoomInIcon aria-hidden="true" />
              {t('inspector.zoom.enlarge')}
            </ToggleButton>
          </div>
        )}
      </div>

      {available ? (
        <>
          {/* It scrolls and zooms: keyboards reach it (arrow keys move it) and its name says what it holds. */}
          <section
            className="inspector-frame"
            ref={frame}
            data-device={device}
            data-zoomed={zoom > 1 || undefined}
            aria-label={t('inspector.frame')}
            aria-describedby="inspector-ayuda"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard.
            tabIndex={0}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
          >
            <div
              ref={canvas}
              className="inspector-canvas"
              style={{
                width: `${zoom * 100}%`,
                minHeight: loadedDevice === device ? undefined : lastCanvasHeight.current || undefined,
              }}
            >
              <img
                key={`${page.key}-${device}-${attempts[device] ?? 0}`}
                src={`${captureUrl(scan.id, page.key, device)}${attempts[device] ? `?intento=${attempts[device]}` : ''}`}
                hidden={status === 'error'}
                alt={t('inspector.alt', {
                  page: page.title || page.finalUrl,
                  device: t(`scan.devices.${device}`),
                })}
                draggable={false}
                onLoad={(event) => {
                  const loaded = event.currentTarget.naturalHeight;
                  setHeights((before) => ({ ...before, [device]: loaded }));
                  setLoadedDevice(device);
                  setLoadState((before) => ({ ...before, [device]: 'loaded' }));
                  onHeight(device, loaded);
                }}
                onError={() => setLoadState((before) => ({ ...before, [device]: 'error' }))}
              />
              {status === 'loading' && (
                <p className="inspector-loading" role="status">
                  {t('inspector.loading')}
                </p>
              )}
              {status === 'error' && (
                <div className="inspector-failed" role="alert">
                  <p>{t('inspector.loadError')}</p>
                  <Button variant="secondary" onPress={reloadCapture}>
                    {t('inspector.retry')}
                  </Button>
                </div>
              )}
              <div
                className="inspector-marks"
                aria-hidden="true"
                data-focused={selected ? true : undefined}
                hidden={status !== 'loaded'}
              >
                {/* The rest of the capture is dimmed around the element chosen, so it is seen at once. */}
                {selectedBox && outcome?.ok && (
                  <span className="inspector-spotlight" style={position(selectedBox)} />
                )}
                {drawn.map((mark) => {
                  const isSelected = mark.element.id === selected?.element.id;
                  const spot = labels.get(mark.element.id);
                  return (
                    <span key={mark.element.id}>
                      {/* A mouse shortcut: keyboards pick elements from the list and the Previous and Next buttons. */}
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the same choice is made from the list by keyboard. */}
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: the same choice is made from the list by keyboard. */}
                      <span
                        className="inspector-mark"
                        data-tone={PRIORITY_TONE[mark.problem.priority]}
                        data-selected={isSelected || undefined}
                        data-dim={selected && !isSelected ? true : undefined}
                        title={`${mark.index + 1} · ${mark.element.name.title}`}
                        style={position(mark.box)}
                        onClick={() => {
                          if (!drag.current?.moved) onSelect(mark.element.id);
                        }}
                      />
                      {selected && spot && height !== null && (
                        <span
                          className="mark-label"
                          data-tone={PRIORITY_TONE[mark.problem.priority]}
                          data-selected={isSelected || undefined}
                          style={{ left: `${(spot.x / width) * 100}%`, top: `${(spot.y / height) * 100}%` }}
                        >
                          {labelText(mark.index, mark.element.name.title, isSelected)}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          </section>
          <p id="inspector-ayuda" className="sr-only">
            {t('inspector.panHint')}
          </p>
        </>
      ) : (
        <p className="inspector-empty">
          {scan.capturesAvailable ? t('inspector.noCapture') : t('inspector.expired')}
        </p>
      )}
    </section>
  );
}
